import React, { useEffect, useMemo, useState } from 'react';

type CompanyDashboardJob = {
  jobId: number;
  jobTitle: string;
  totalPositions: number;
  positionsFilled: number;
  remainingPositions: number;
  totalApplicants: number;
  acceptedApplicants: number;
  jobStatus: string;
  jobRating: number | null;
};

type CompanyDashboardData = {
  totalJobsCreated: number;
  totalJobsOpen: number;
  totalJobsClosed: number;
  positionsPosted: number;
  positionsFilled: number;
  remainingPositions: number;
  totalApplicants: number;
  acceptedApplicants: number;
  companyRating: number | null;
  totalReviews: number;
  funnel: {
    jobsPosted: number;
    applicationsReceived: number;
    applicantsAccepted: number;
    jobsCompleted: number;
  };
  companyRatingDetails?: Array<{
    applicantName: string;
    jobTitle: string;
    rating: number;
    comment: string | null;
    createdAt: string;
  }>;
  jobs: CompanyDashboardJob[];
};

type Props = Readonly<{
  loading: boolean;
  data: CompanyDashboardData | null;
  onOpenJob: (jobId: number) => void;
}>;

const metricItems = (data: CompanyDashboardData) => [
  ['Total Jobs Created', data.totalJobsCreated],
  ['Jobs Open', data.totalJobsOpen],
  ['Jobs Closed', data.totalJobsClosed],
  ['Positions Posted', data.positionsPosted],
  ['Positions Filled', data.positionsFilled],
  ['Remaining Positions', data.remainingPositions],
  ['Total Applicants', data.totalApplicants],
  ['Accepted Applicants', data.acceptedApplicants],
  ['Company Rating', data.companyRating ? `⭐ ${data.companyRating.toFixed(1)} (${data.totalReviews})` : '⭐ New'],
] as const;

export default function CompanyDashboard({ loading, data, onOpenJob }: Props) {
  const [activeMetric, setActiveMetric] = useState<string | null>(null);
  const [jobTablePage, setJobTablePage] = useState(1);
  const [metricTablePage, setMetricTablePage] = useState(1);
  const PAGE_SIZE = 10;

  const modalRows = useMemo(() => {
    if (!activeMetric || !data) return [] as Array<Record<string, React.ReactNode>>;

    if (activeMetric === 'Company Rating') {
      return (data.companyRatingDetails ?? []).map((review) => ({
        'Applicant Name': review.applicantName,
        'Job Title': review.jobTitle,
        Rating: `⭐ ${review.rating.toFixed(1)}`,
        Comments: review.comment?.trim() ? review.comment : '—',
      }));
    }

    if (activeMetric === 'Total Applicants' || activeMetric === 'Accepted Applicants' || activeMetric === 'Positions Filled') {
      return data.jobs.map((job) => ({
        'Job ID': job.jobId,
        'Job Title': job.jobTitle,
        Applicants: job.totalApplicants,
        Accepted: job.acceptedApplicants,
        'Filled Positions': job.positionsFilled,
        Status: job.jobStatus,
      }));
    }

    return data.jobs.map((job) => ({
      'Job ID': job.jobId,
      'Job Title': job.jobTitle,
      'Total Positions': job.totalPositions,
      Remaining: job.remainingPositions,
      Status: job.jobStatus,
    }));
  }, [activeMetric, data]);

  const modalColumns = modalRows.length > 0 ? Object.keys(modalRows[0]) : [];
  const totalJobPages = Math.max(1, Math.ceil((data?.jobs.length ?? 0) / PAGE_SIZE));
  const pagedJobs = (data?.jobs ?? []).slice((jobTablePage - 1) * PAGE_SIZE, jobTablePage * PAGE_SIZE);
  const totalMetricPages = Math.max(1, Math.ceil(modalRows.length / PAGE_SIZE));
  const pagedModalRows = modalRows.slice((metricTablePage - 1) * PAGE_SIZE, metricTablePage * PAGE_SIZE);
  const closeMetricModal = () => {
    setActiveMetric(null);
    setMetricTablePage(1);
  };

  useEffect(() => {
    setMetricTablePage(1);
  }, [activeMetric]);

  useEffect(() => {
    setJobTablePage((current) => Math.min(current, totalJobPages));
  }, [totalJobPages]);

  if (loading) {
    return <p className="empty-inline">Loading company dashboard...</p>;
  }

  if (!data) {
    return <p className="empty-inline">No dashboard data available.</p>;
  }

  return (
    <div className="company-jobs-list">
      <div className="applicants-list" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12 }}>
        {metricItems(data).map(([label, value]) => (
          <button
            type="button"
            key={label}
            className="applicant-card"
            onClick={() => setActiveMetric(label)}
            style={{ textAlign: 'left', cursor: 'pointer' }}
          >
            <div className="applicant-info">
              <span>{label}</span>
              <strong>{value as React.ReactNode}</strong>
            </div>
          </button>
        ))}
      </div>

      <div className="company-job-card">
        <div className="company-job-header" style={{ cursor: 'default' }}>
          <div className="company-job-info">
            <h3>Hiring Funnel</h3>
            <div className="company-job-meta">
              <span>Jobs Posted: {data.funnel.jobsPosted}</span>
              <span>Applications: {data.funnel.applicationsReceived}</span>
              <span>Accepted: {data.funnel.applicantsAccepted}</span>
              <span>Completed: {data.funnel.jobsCompleted}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="company-job-card">
        <div className="company-job-header" style={{ cursor: 'default' }}>
          <div className="company-job-info">
            <h3>Job Performance</h3>
          </div>
        </div>
        <div className="applicants-section" style={{ overflowX: 'auto' }}>
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Job Title</th>
                <th>Total Positions</th>
                <th>Filled Positions</th>
                <th>Remaining</th>
                <th>Total Applicants</th>
                <th>Accepted Applicants</th>
                <th>Status</th>
                <th>Job Rating</th>
              </tr>
            </thead>
            <tbody>
              {pagedJobs.map((job) => (
                <tr key={job.jobId} onClick={() => onOpenJob(job.jobId)} style={{ cursor: 'pointer' }}>
                  <td>{job.jobId}</td>
                  <td>{job.jobTitle}</td>
                  <td>{job.totalPositions}</td>
                  <td>{job.positionsFilled}</td>
                  <td>{job.remainingPositions}</td>
                  <td>{job.totalApplicants}</td>
                  <td>{job.acceptedApplicants}</td>
                  <td>{job.jobStatus}</td>
                  <td>{job.jobRating ? `⭐ ${job.jobRating.toFixed(1)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.jobs.length > PAGE_SIZE ? (
            <div className="applicants-pagination">
              <button
                className="btn-ghost"
                disabled={jobTablePage <= 1}
                onClick={() => setJobTablePage((page) => Math.max(1, page - 1))}
              >
                Previous
              </button>
              <span>Page {jobTablePage} / {totalJobPages}</span>
              <button
                className="btn-ghost"
                disabled={jobTablePage >= totalJobPages}
                onClick={() => setJobTablePage((page) => Math.min(totalJobPages, page + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {activeMetric && (
        <div className="modal-overlay">
          <button
            type="button"
            className="modal-overlay-hitbox"
            onClick={closeMetricModal}
            aria-label="Close metric details"
          />
          <dialog
            className="modal modal-wide modal-dialog-open"
            open
            onCancel={(event) => {
              event.preventDefault();
              closeMetricModal();
            }}
          >
            <button className="modal-close" onClick={closeMetricModal}>×</button>
            <h2 className="modal-form-title">{activeMetric} Details</h2>
            {activeMetric === 'Company Rating' ? (
              <p className="empty-inline" style={{ marginBottom: 12 }}>
                Overall Average: {data.companyRating ? `⭐ ${data.companyRating.toFixed(1)}` : '⭐ New'} ({data.totalReviews} review{data.totalReviews === 1 ? '' : 's'})
              </p>
            ) : null}
            <div style={{ overflowX: 'auto' }}>
              <table className="dashboard-table">
                <thead>
                  <tr>
                    {modalColumns.map((column) => (
                      <th key={column}>{column}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedModalRows.map((row) => {
                    const rowKey = JSON.stringify(row);
                    return (
                      <tr key={`metric-row-${rowKey}`}>
                        {modalColumns.map((column) => (
                          <td key={`${rowKey}-${column}`}>{row[column]}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {modalRows.length > PAGE_SIZE ? (
              <div className="applicants-pagination">
                <button
                  className="btn-ghost"
                  disabled={metricTablePage <= 1}
                  onClick={() => setMetricTablePage((page) => Math.max(1, page - 1))}
                >
                  Previous
                </button>
                <span>Page {metricTablePage} / {totalMetricPages}</span>
                <button
                  className="btn-ghost"
                  disabled={metricTablePage >= totalMetricPages}
                  onClick={() => setMetricTablePage((page) => Math.min(totalMetricPages, page + 1))}
                >
                  Next
                </button>
              </div>
            ) : null}
          </dialog>
        </div>
      )}
    </div>
  );
}
